<template>
  <footer class="pycad-statusbar">
    <!-- Left: Patient info -->
    <div class="pycad-statusbar__left">
      <span v-if="patientLine">{{ patientLine }}</span>
      <span v-else class="pycad-statusbar__muted">{{ emptyLabel }}</span>
    </div>

    <!-- Center: slices / W/L / FOV / loading -->
    <div class="pycad-statusbar__center">
      <template v-for="slice in axisSlices" :key="slice.label">
        <span
          :class="[
            'pycad-statusbar__axis',
            `pycad-statusbar__axis--${slice.cssKey}`,
          ]"
        >
          {{ slice.abbrev }} {{ slice.current }}/{{ slice.total }}
        </span>
      </template>
      <span v-if="axisSlices.length" class="pycad-statusbar__divider">|</span>

      <template v-if="hasWindowLevel">
        <span>W: {{ formattedWindow }}</span>
        <span>L: {{ formattedLevel }}</span>
      </template>

      <template v-if="isImageLoading">
        <span class="pycad-statusbar__divider">|</span>
        <span class="pycad-statusbar__loading">
          <i class="mdi mdi-loading pycad-statusbar__spin"></i>
          Loading
        </span>
      </template>
    </div>

    <!-- Right: coord system + server connection -->
    <div class="pycad-statusbar__right">
      <span class="pycad-statusbar__coord">{{ coordinateSystem }}</span>
      <span class="pycad-statusbar__divider">|</span>
      <div class="pycad-statusbar__connection">
        <span
          class="pycad-statusbar__dot"
          :style="{ background: connColor }"
          :class="{ 'pycad-pulse': connState === ConnectionState.Connected }"
        ></span>
        <span :style="{ color: connColor }">{{ connLabel }}</span>
      </div>
    </div>
  </footer>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { storeToRefs } from 'pinia';

import { useCurrentImage, useImage } from '@/src/composables/useCurrentImage';
import { useViewStore } from '@/src/store/views';
import { useDICOMStore } from '@/src/store/datasets-dicom';
import { isDicomImage } from '@/src/utils/dataSelection';
import useViewSliceStore from '@/src/store/view-configs/slicing';
import { useWindowingStore } from '@/src/store/view-configs/windowing';
import { ConnectionState, useServerStore } from '@/src/store/server';
import { usePycadUiStore } from '@/src/store/pycad-ui';
import { useAppConfigStore } from '@/src/store/app-config';
import { getStatusBarPatientLine } from '@/src/utils/dicomDisplay';

interface AxisSlice {
  abbrev: string;
  label: string;
  cssKey: 'axial' | 'sagittal' | 'coronal';
  current: number;
  total: number;
}

// --- Active image / metadata --- //
const { currentImageID, currentImageMetadata, isImageLoading } =
  useCurrentImage('global');

const dicomStore = useDICOMStore();
const appConfigStore = useAppConfigStore();

const emptyLabel = computed(() =>
  appConfigStore.anonymize ? 'No data loaded' : 'No patient loaded'
);

const patientLine = computed(() => {
  const id = currentImageID.value;
  if (!id) return '';
  if (!isDicomImage(id)) {
    return appConfigStore.anonymize
      ? ''
      : currentImageMetadata.value?.name || '';
  }
  const volumeInfo = dicomStore.volumeInfo[id];
  if (!volumeInfo) return '';
  const studyKey = dicomStore.volumeStudy[id];
  const study = studyKey ? dicomStore.studyInfo[studyKey] : undefined;
  const patientKey = studyKey ? dicomStore.studyPatient[studyKey] : undefined;
  const patient = patientKey ? dicomStore.patientInfo[patientKey] : undefined;

  return getStatusBarPatientLine({
    patient,
    study,
    modality: volumeInfo.Modality,
  });
});

// --- Per-axis slice readouts (from visibleViews) --- //
const viewStore = useViewStore();
const { visibleViews } = storeToRefs(viewStore);
const sliceStore = useViewSliceStore();

const ORIENTATION_INFO: Record<
  string,
  { abbrev: string; cssKey: AxisSlice['cssKey'] }
> = {
  Axial: { abbrev: 'Ax', cssKey: 'axial' },
  Sagittal: { abbrev: 'Sa', cssKey: 'sagittal' },
  Coronal: { abbrev: 'Co', cssKey: 'coronal' },
};

const axisSlices = computed<AxisSlice[]>(() => {
  if (!currentImageID.value) return [];
  const seen = new Set<string>();
  const out: AxisSlice[] = [];
  for (const view of visibleViews.value) {
    if (!view || view.type !== '2D') continue;
    const orientation = (view.options as { orientation?: string } | undefined)
      ?.orientation;
    if (!orientation) continue;
    const meta = ORIENTATION_INFO[orientation];
    if (!meta) continue;
    if (seen.has(orientation)) continue;
    seen.add(orientation);

    const cfg = sliceStore.getConfig(view.id, currentImageID.value);
    out.push({
      abbrev: meta.abbrev,
      label: orientation,
      cssKey: meta.cssKey,
      current: cfg.slice + 1,
      total: cfg.max + 1,
    });
  }
  return out;
});

// --- Window / Level for the active view --- //
const { activeView } = storeToRefs(viewStore);
const windowingStore = useWindowingStore();

const wlConfig = computed(() => {
  if (!activeView.value || !currentImageID.value) return undefined;
  return windowingStore.getConfig(activeView.value, currentImageID.value);
});

const hasWindowLevel = computed(() => wlConfig.value !== undefined);
const formattedWindow = computed(() => {
  const w = wlConfig.value?.width;
  return w == null ? '—' : Math.round(w).toString();
});
const formattedLevel = computed(() => {
  const l = wlConfig.value?.level;
  return l == null ? '—' : Math.round(l).toString();
});

// Touch the metadata composable so it stays subscribed (suppresses unused warning).
useImage(currentImageID);

// --- Coordinate system --- //
const ui = usePycadUiStore();
const { coordinateSystem } = storeToRefs(ui);

// --- Server connection --- //
const serverStore = useServerStore();
const { url: serverUrl, connState } = storeToRefs(serverStore);

const connColor = computed(() => {
  if (!serverUrl.value) return '#666666';
  switch (connState.value) {
    case ConnectionState.Connected:
      return '#3B82F6';
    case ConnectionState.Pending:
      return '#ff9900';
    case ConnectionState.Disconnected:
    default:
      return '#FF5C5C';
  }
});

const connLabel = computed(() => {
  if (!serverUrl.value) return 'Local';
  switch (connState.value) {
    case ConnectionState.Connected:
      return 'Live';
    case ConnectionState.Pending:
      return 'Connecting';
    case ConnectionState.Disconnected:
    default:
      return 'Offline';
  }
});
</script>

<style scoped>
.pycad-statusbar {
  display: flex;
  align-items: center;
  height: var(--pycad-statusbar-height, 24px);
  padding: 0 12px;
  gap: 16px;
  background: var(--pycad-surface);
  border-top: 1px solid var(--pycad-border);
  flex-shrink: 0;
  font-family: 'JetBrains Mono', 'Fira Code', 'Consolas', monospace;
  font-size: 11px;
  font-variant-numeric: tabular-nums;
  color: var(--pycad-text-muted);
  overflow: hidden;
  user-select: none;
}

.pycad-statusbar__left {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--pycad-text-secondary);
}

.pycad-statusbar__muted {
  opacity: 0.6;
}

.pycad-statusbar__center {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}

.pycad-statusbar__axis--axial {
  color: var(--pycad-axial);
}
.pycad-statusbar__axis--sagittal {
  color: var(--pycad-sagittal);
}
.pycad-statusbar__axis--coronal {
  color: var(--pycad-coronal);
}

.pycad-statusbar__divider {
  color: var(--pycad-border);
}

.pycad-statusbar__loading {
  color: var(--pycad-primary);
  display: flex;
  align-items: center;
  gap: 4px;
}

.pycad-statusbar__spin {
  font-size: 10px;
  animation: pycad-spin 1s linear infinite;
}

.pycad-statusbar__right {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.pycad-statusbar__coord {
  color: var(--pycad-text-secondary);
}

.pycad-statusbar__connection {
  display: flex;
  align-items: center;
  gap: 4px;
}

.pycad-statusbar__dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
}
</style>
